import { IconPlusCircle } from "@/assets/icons";
import { IconOrderTable } from "@/assets/icons/IconOrderTable";
import { Button } from "@/components/elements/Button";
import { DATE_TIME_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { CertificateIndustryRelationFormModal } from "@/features/master-data/components/SpecificSkillCertificate/CertificateIndustryRelationFormModal";
import { HiddenCertificateIndustryRelationModal } from "@/features/master-data/components/SpecificSkillCertificate/HiddenCertificateIndustryRelationModal";
import { useVisible } from "@/hooks/useVisible";
import { background, blue, button, divider, green, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { dayjs } from "@/utils/dayjs";
import { Box, Stack, styled, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { FC, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { TGetSpecificSkillCertificateCompanySpecificIndustryOutputDto } from "@/api/services/global/master/dtos/specificSkillCertificate/get-master-specific-skill-certificate-list.dto";

type TCertificateIndustryRelationFormTableProps = {
  selectedCertificateIndustryRelationRows: TGetSpecificSkillCertificateCompanySpecificIndustryOutputDto[];
  setSelectedCertificateIndustryRelationRows: React.Dispatch<React.SetStateAction<TGetSpecificSkillCertificateCompanySpecificIndustryOutputDto[]>>;
  existingSpecificSkillCertificateId: number;
  existingSpecificSkillCertificate: string;
};

export const CertificateIndustryRelationFormTable: FC<TCertificateIndustryRelationFormTableProps> = ({
  selectedCertificateIndustryRelationRows,
  setSelectedCertificateIndustryRelationRows,
  existingSpecificSkillCertificateId,
  existingSpecificSkillCertificate,
}) => {
  const certificateIndustryRelationFormModal = useVisible();
  const [hiddenCertificateIndustryRelation, setHiddenCertificateIndustryRelation] =
    useState<TGetSpecificSkillCertificateCompanySpecificIndustryOutputDto>();
  const [selectedCertificateIndustryRelation, setSelectedCertificateIndustryRelation] =
    useState<TGetSpecificSkillCertificateCompanySpecificIndustryOutputDto>();

  // Handle row dragging
  const onDragEnd = (result: DropResult) => {
    if (!result.destination || !result.source || result.source.index === result.destination.index) return;
    const reorderedRows = Array.from(selectedCertificateIndustryRelationRows);
    const [removed] = reorderedRows.splice(result.source.index, 1);
    reorderedRows.splice(result.destination.index, 0, removed);
    setSelectedCertificateIndustryRelationRows(reorderedRows);
  };

  return (
    <>
      <Stack gap={1} flexDirection="column">
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="tableRows" direction="vertical">
            {(provided) => (
              <StyledTable {...provided.droppableProps} ref={provided.innerRef}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell>分野</TableCell>
                      <TableCell>最終更新日</TableCell>
                      <TableCell>有効／無効</TableCell>
                      <TableCell>編集</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedCertificateIndustryRelationRows.map((row, index) => (
                      <Draggable key={row.id} draggableId={row.id.toString()} index={index}>
                        {(provided) => (
                          <TableRow ref={provided.innerRef} {...provided.draggableProps}>
                            <TableCell sx={{ width: "3%", paddingLeft: "10px !important" }} {...provided.dragHandleProps}>
                              <IconOrderTable color={blue[80]} />
                            </TableCell>
                            <TableCell sx={{ width: "40%" }}>
                              <Typography color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                                {row.companySpecificIndustry?.companySpecificIndustry || EMPTY_STR.TEXT}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ width: "35%" }}>
                              <Typography color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                                {row.updatedAt ? dayjs(row.updatedAt).format(DATE_TIME_FORMAT) : EMPTY_STR.DATE}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ width: "15%" }}>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setHiddenCertificateIndustryRelation(row);
                                }}
                              >
                                {row.isHidden ? "有効化" : "無効化"}
                              </Button>
                            </TableCell>
                            <TableCell sx={{ width: "10%" }}>
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedCertificateIndustryRelation(row);
                                  certificateIndustryRelationFormModal.open();
                                }}
                              >
                                編集
                              </Button>
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableBody>
                </Table>
              </StyledTable>
            )}
          </Droppable>
        </DragDropContext>
        <AddRowButton onClick={() => certificateIndustryRelationFormModal.open()}>
          <IconPlusCircle />
          <Typography variant="cap12">{"行を追加する"}</Typography>
        </AddRowButton>
      </Stack>

      {certificateIndustryRelationFormModal.visible && (
        <CertificateIndustryRelationFormModal
          onClose={() => {
            certificateIndustryRelationFormModal.close();
            setSelectedCertificateIndustryRelation(undefined);
          }}
          isOpen={certificateIndustryRelationFormModal.visible}
          selectedCertificateIndustryRelation={selectedCertificateIndustryRelation}
          existingSpecificSkillCertificate={existingSpecificSkillCertificate}
          existingSpecificSkillCertificateId={existingSpecificSkillCertificateId}
          setSelectedCertificateIndustryRelationRows={setSelectedCertificateIndustryRelationRows}
        />
      )}

      <HiddenCertificateIndustryRelationModal
        hiddenCertificateIndustryRelation={hiddenCertificateIndustryRelation}
        setHiddenCertificateIndustryRelation={setHiddenCertificateIndustryRelation}
        setSelectedCertificateIndustryRelationRows={setSelectedCertificateIndustryRelationRows}
      />
    </>
  );
};

const AddRowButton = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

const StyledTable = styled(Box)`
  border-radius: ${rounded.sm};
  overflow: auto;
  max-height: 35vh;
  height: fit-content !important;
  border: 1px solid ${divider.middle};
  background-color: ${background.white};
  table {
    border-collapse: separate;
  }

  * {
    box-shadow: none;
  }
  .MuiToolbar-dense {
    display: none;
  }
  .MuiTableCell-head {
    padding: 8px !important;
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    text-align: left;
    position: sticky;
    background-color: white;
    z-index: 500;
    top: 0;
  }

  .Mui-TableHeadCell-Content-Labels {
    > span {
      margin-left: 4px;
      img {
        opacity: 0.2;
      }
      .MuiBadge-badge {
        display: none;
      }
    }
  }
  .MuiTableCell-body {
    font-size: 14px;
    font-weight: 400;
    line-height: 28px;
  }

  // Default
  .MuiTableRow-root {
    background-color: ${background.white};
    box-shadow: none;
    cursor: initial;
    .MuiTableCell-root {
      padding: 4px;
      border-color: ${divider.middle};
      opacity: 1;
    }
    &:not(.MuiTableRow-head):last-child {
      .MuiTableCell-root {
        border-bottom: 0;
      }
    }
  }
  // Hover
  .MuiTableCell-root:not(.MuiTableRow-head):hover {
    &,
    td {
      background-color: ${background.white};
      &::after {
        background-color: ${background.primary};
      }
    }
  }
  // Click/Pressed
  .Mui-selected {
    background-color: ${background.white} !important;
    td::after {
      background: ${background.secondary}!important;
    }
  }

  .MuiPaper-root {
    box-shadow: none;
  }
  .MuiPaper-root > .MuiBox-root {
    display: none;
  }

  .MuiCheckbox-root {
    color: ${button.primary}!important;
    height: 24px;
    width: 24px;
    padding: 0;
  }

  // Disabled Row
  .MuiTableCell-root.disabled {
    opacity: 1;
    background: ${background.disable}!important;
    pointer-events: none;
  }

  // added row
  .MuiTableCell-root.added {
    background: ${green[99]}!important;
    &::after {
      background: unset !important;
    }
  }

  // Failed row
  .MuiTableCell-root.failed {
    background: ${background.error}!important;
    &::after {
      background: unset !important;
    }
  }

  // Clickable row
  .MuiTableCell-root.clickable {
    cursor: pointer;
  }
`;
